/**
 * DirectionGauge.jsx — dashboard/DirectionGauge.jsx
 *
 * Composite open-direction signal derived from:
 *   - ES=F (S&P 500 E-mini futures) — weight 60% — the primary signal
 *   - ^VIX (fear index)             — weight 25% — inverted (rising VIX = bearish)
 *   - SPY pre-market %              — weight 15% — confirmation
 *
 * Score: -100 (strongly bearish) to +100 (strongly bullish)
 * Confidence: how much of the 100% weight had available data
 *
 * NOTE: This is a heuristic glance signal, not a prediction. Weights are in
 * src/lib/indicators.js and easy to tune.
 */

import { openDirectionScore } from '../../lib/indicators';
import { fmtPrice, fmtPct } from '../../lib/format';
import { IndicatorTooltip } from '../shared/IndicatorTooltip';

export default function DirectionGauge({ esQuote, vixQuote, spyQuote }) {
  const esPct        = esQuote?.regularMarketChangePercent  ?? null;
  const vixPct       = vixQuote?.regularMarketChangePercent ?? null;
  const spyPreMktPct = spyQuote?.preMarketChangePercent     ?? null;

  const { score, label, confidence } = openDirectionScore({ esPct, vixPct, spyPreMktPct });

  // Bar fill: green right-of-centre for bullish, red left-of-centre for bearish
  const color = score == null ? 'var(--muted)'
    : score >  15 ? 'var(--up)'
    : score < -15 ? 'var(--down)'
    : 'var(--muted)';

  const fillStyle = score == null ? { display: 'none' }
    : score >= 0
      ? { left: '50%', width: `${(score / 100) * 50}%`, background: 'var(--up)' }
      : { right: '50%', width: `${(-score / 100) * 50}%`, background: 'var(--down)' };

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      {/* Score + label */}
      <div className="gauge">
        <div>
          <div className="gauge-score-row">
            <div className="gauge-score" style={{ color }}>
              {score == null ? '—' : (score > 0 ? '+' : '') + score}
            </div>
            <div className="gauge-label" style={{ color }}>{label}</div>
          </div>
          {/* Visual bar — midpoint = 0, left = bearish, right = bullish */}
          <div className="gauge-bar-wrap" style={{ marginTop: 10 }}>
            <div className="gauge-bar">
              <div className="midline" />
              <div className="gauge-fill" style={fillStyle} />
            </div>
          </div>
          <div className="gauge-conf" style={{ marginTop: 4 }}>
            CONFIDENCE {confidence}%
          </div>
        </div>
      </div>

      {/* Input rows */}
      <div className="gauge-rows">
        <GaugeRow lbl="ES=F (futures)" pct={esPct}        price={esQuote?.regularMarketPrice}  />
        <GaugeRow lbl="^VIX (fear)"   pct={vixPct}        price={vixQuote?.regularMarketPrice} inverse />
        <GaugeRow lbl="SPY pre-mkt"   pct={spyPreMktPct}  price={spyQuote?.preMarketPrice}     />
        <GaugeRow lbl="SPY last"      pct={spyQuote?.regularMarketChangePercent} price={spyQuote?.regularMarketPrice} />
      </div>
    </div>
  );
}

/** One data row inside the gauge */
function GaugeRow({ lbl, pct, price, inverse }) {
  // For VIX (inverse), a positive VIX move is bearish for the market
  const dirColor = pct == null ? 'var(--muted)'
    : (inverse ? -pct : pct) > 0 ? 'var(--up)' : 'var(--down)';
  return (
    <div className="stat-row">
      <span className="stat-lbl">{lbl}</span>
      <span>
        <span className="stat-val">{fmtPrice(price)}</span>
        <span className="stat-sub" style={{ color: dirColor }}>{fmtPct(pct)}</span>
      </span>
    </div>
  );
}
